import React, { useEffect, useState } from "react";
import Api from "../../config/Api";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  Chart,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Legend,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
// import Api from "../../config/Api";
// import { BsDownload } from "react-icons/bs";
import {
  FaHourglassStart,
  FaHourglassHalf,
  FaCheckDouble,
  FaRupeeSign,
  FaTimes,
  FaCheckSquare,
  FaLeaf,
} from "react-icons/fa";

Chart.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Legend,
  Title,
  Tooltip
);

Chart.defaults.font.family = '"Public Sans", -apple-system, "Segoe UI", Roboto, Arial, sans-serif';

const KPIS = [
  { key: "pending", label: "Pending SDRF", icon: <FaHourglassStart />, tone: "warn" },
  { key: "rejected", label: "Rejected SDRF", icon: <FaTimes />, tone: "bad" },
  { key: "verified", label: "Verified SDRF", icon: <FaCheckSquare />, tone: "info" },
  { key: "approved", label: "Approved SDRF", icon: <FaCheckDouble />, tone: "brand" },
  { key: "under_process", label: "Under Process SDRF", icon: <FaHourglassHalf />, tone: "warn" },
  { key: "released", label: "Released SDRF", icon: <FaRupeeSign />, tone: "ok" },
  { key: "paid_from_erf", label: "Paid from ERF", icon: <FaLeaf />, tone: "info" },
];

// Largest first, so the ranking reads straight down the chart.
const rankRows = (rows) =>
  rows
    .map(([label, value]) => [label, Number(value) || 0])
    .sort((a, b) => b[1] - a[1]);

const RankChart = ({ title, rows, chartColors }) => {
  const data = {
    labels: rows.map((r) => r[0]),
    datasets: [
      {
        data: rows.map((r) => r[1]),
        backgroundColor: chartColors.bar,
        maxBarThickness: 16,
        borderRadius: { topRight: 4, bottomRight: 4 },
        borderSkipped: false,
      },
    ],
  };
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { left: 6, right: 44 } },
    scales: {
      x: { display: false, beginAtZero: true, grid: { display: false } },
      y: {
        grid: { display: false, drawBorder: false },
        ticks: { color: chartColors.text, font: { size: 12 }, autoSkip: false },
      },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: "end",
        align: "end",
        clamp: true,
        color: chartColors.text,
        font: { size: 12, weight: 600 },
        formatter: (value) => value.toLocaleString("en-IN"),
      },
    },
  };

  return (
    <div className="chart-panel">
      <div className="chart-panel-head">
        <h5>{title}</h5>
      </div>
      <div className="rankchartbox">
        {rows.length > 0 ? (
          <Bar options={options} data={data} plugins={[ChartDataLabels]} />
        ) : (
          <div className="chart-empty">No data available</div>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [counters, setCounters] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [barData, setBarData] = useState([]);
  const [isLoading, setIsLoading] = useState([]);

  const [pieData, setPieData] = useState([]);
  const [isDark, setIsDark] = useState(localStorage.getItem("theme") === "dark");

  // Canvas colours can't follow CSS variables, so redraw when the theme toggles.
  useEffect(() => {
    const html = document.documentElement;
    const observer = new MutationObserver(() =>
      setIsDark(html.getAttribute("data-theme") === "dark")
    );
    observer.observe(html, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  // Axis labels are measured on first draw; re-measure once the web font is in.
  useEffect(() => {
    document.fonts?.ready.then(() =>
      Object.values(Chart.instances).forEach((chart) => chart.update())
    );
  }, []);

  const chartColors = isDark
    ? { bar: "#5b93e6", text: "#a3b1c7", grid: "#2a3853" }
    : { bar: "#1f4f96", text: "#5d6b80", grid: "#e2e8f0" };

  const getDistrict = async () => {
    try {
      setIsLoading(true);
      const { data } = await Api("get-city/1");
      //console.log(data);
      if (data?.status === 1) {
        setDistrictList(data?.districts);
        setIsLoading(false);
        return true;
      } else {
        toast.error(data?.message, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setIsLoading(false);
      }
    } catch (error) {
      toast.error(error.message, {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };
  const getCounters = async () => {
    const res = await Api("dashboard");
    // console.log(res["data"]["data"][0]);
    if (res.data.status === 1) {
      setCounters(res.data.data[0]);
    }
  };
  const getBardata = async (did) => {
    const body = {};
    if (Boolean(did)) {
      body.district_id = did;
    }
    const { data } = await Api("linechart", "POST", body);
    // console.log(data);
    if (data.status === 1) {
      setBarData(data.data);
    }
  };
  const updateLineChart = (e) => {
    getBardata(e.target.value);
  };
  const getpieData = async () => {
    try {
      const { data } = await Api("piechart");
      if (data.status === 1) {
        setPieData(data.data);
      }
    } catch (error) {
      // Keep the dashboard usable even if the chart data fails to load.
    }
  };
  useEffect(() => {
    getCounters();
    getDistrict();
    getBardata();
    getpieData();
  }, []);

  const labels = barData?.map((a) =>
    dayjs(a.month, "MMMM YYYY").format("MMM YYYY")
  );
  const dataCounts = barData?.map((a) => Number(a.number_of_applications));

  const districtRows = rankRows(
    (pieData?.districts || []).map((a) => [a?.district_name, a?.districts])
  );
  const damageRows = rankRows(
    (pieData?.itemsdamage || []).map((a) => [a?.title, a?.No_of_first_damage])
  );
  const natureRows = rankRows(Object.entries(pieData?.naturedisaster || {}));

  const data1 = {
    labels: labels,
    datasets: [
      {
        data: dataCounts,
        backgroundColor: chartColors.bar,
        maxBarThickness: 24,
        borderRadius: { topLeft: 4, topRight: 4 },
        borderSkipped: false,
      },
    ],
  };
  const options1 = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { color: chartColors.text },
      },
      y: {
        beginAtZero: true,
        grid: { color: chartColors.grid, drawBorder: false },
        ticks: { color: chartColors.text, precision: 0 },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  return (
    <>
      <div className="kpi-grid">
        {KPIS.map((kpi) => (
          <div className={`kpi-tile kpi-${kpi.tone}`} key={kpi.key}>
            <div className="kpi-head">
              <p className="kpi-label">{kpi.label}</p>
              <div className="kpi-icon">{kpi.icon}</div>
            </div>
            <p className="kpi-value">
              {Number(counters?.[kpi.key] || 0).toLocaleString("en-IN")}
            </p>
          </div>
        ))}
      </div>

      <div className="chart-panel mb-3">
        <div className="chart-panel-head">
          <h5>SDRF Applications by Month</h5>
          <select
            className="form-select form-select-sm"
            onChange={updateLineChart}
            disabled={isLoading}
          >
            <option value="">All Districts</option>
            {districtList?.map((item, index) => {
              return (
                <option value={item.id} key={index}>
                  {item?.district_name}
                </option>
              );
            })}
          </select>
        </div>
        <div className="bargraphbox">
          <Bar options={options1} data={data1} />
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-4">
          <RankChart title="SDRF per District" rows={districtRows} chartColors={chartColors} />
        </div>
        <div className="col-lg-4">
          <RankChart title="SDRF by Nature of Disaster" rows={natureRows} chartColors={chartColors} />
        </div>
        <div className="col-lg-4">
          <RankChart title="SDRF by Item Damaged" rows={damageRows} chartColors={chartColors} />
        </div>
      </div>
    </>
  );
};

export default Dashboard;
